import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  Spinner,
  Center,
  Button,
  Select,
  HStack,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// Interface for analytics data
interface AnalyticsData {
  modelStats: {
    name: string;
    count: number;
    avgSimilarity: number;
  }[];
  sourceStats: {
    name: string;
    count: number;
    value: number;
  }[];
  similarityDistribution: {
    range: string;
    count: number;
  }[];
  consensusStats: {
    modelFamilies: number;
    count: number;
  }[];
  totalMatches: number;
  confirmedMatches: number;
  rejectedMatches: number;
  modelComparison: {
    name: string;
    confirmedCount: number;
    rejectedCount: number;
    confirmedAvgSimilarity: number;
    rejectedAvgSimilarity: number;
    highestRejectedSimilarity: number;
    highestRejectedId: string;
    highestRejectedConsensus: number;
    lowestConfirmedSimilarity: number;
    lowestConfirmedId: string;
    lowestConfirmedConsensus: number;
  }[];
  consensusComparison: {
    modelFamilies: number;
    confirmedCount: number;
    rejectedCount: number;
  }[];
  confirmedAvgSimilarity: number;
  rejectedAvgSimilarity: number;
  highestRejectedMatch: {
    id: string;
    similarity: number;
    model: string;
    consensus: number;
  } | null;
  lowestConfirmedMatch: {
    id: string;
    similarity: number;
    model: string;
    consensus: number;
  } | null;
  rawMatches: {
    similarity: number;
    consensus: number;
    confirmed: boolean;
  }[];
}

export const MatchAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<string>('all');
  const toast = useToast();

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      console.log('Fetching analytics data...');
      const response = await fetch(`/insight/api/admin/match-analytics?timeRange=${timeRange}`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Received analytics data:', data);
      setAnalyticsData(data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load analytics data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Center p={10}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!analyticsData) {
    return (
      <Box p={6}>
        <Heading size="lg" mb={6}>Match Analytics</Heading>
        <Text>No analytics data available.</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="lg">Match Analytics</Heading>
        <HStack spacing={4}>
          <Select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            width="200px"
          >
            <option value="all">All Time</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last 3 Months</option>
          </Select>
          <Button colorScheme="blue" onClick={fetchAnalyticsData}>
            Refresh
          </Button>
        </HStack>
      </Flex>

      {/* Summary Stats */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Lowest Confirmed Similarity</StatLabel>
              <StatNumber>
                {analyticsData.lowestConfirmedMatch 
                  ? `${(analyticsData.lowestConfirmedMatch.similarity * 100).toFixed(1)}%`
                  : 'N/A'}
              </StatNumber>
              <StatHelpText>
                {analyticsData.lowestConfirmedMatch 
                  ? `${analyticsData.lowestConfirmedMatch.model} (${analyticsData.lowestConfirmedMatch.consensus} families)`
                  : ''}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Highest Rejected Similarity</StatLabel>
              <StatNumber>
                {analyticsData.highestRejectedMatch 
                  ? `${(analyticsData.highestRejectedMatch.similarity * 100).toFixed(1)}%`
                  : 'N/A'}
              </StatNumber>
              <StatHelpText>
                {analyticsData.highestRejectedMatch 
                  ? `${analyticsData.highestRejectedMatch.model} (${analyticsData.highestRejectedMatch.consensus} families)`
                  : ''}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Similarity Thresholds by Consensus</StatLabel>
              <StatNumber>
                {(() => {
                  const calculateThreshold = (consensusLevel: number) => {
                    const matchData = analyticsData.rawMatches;
                    
                    const similaritiesConfirmed = matchData
                      .filter((match: { confirmed: boolean; consensus: number }) => match.confirmed && match.consensus === consensusLevel)
                      .map((match: { similarity: number }) => match.similarity);
                    const similaritiesRejected = matchData
                      .filter((match: { confirmed: boolean; consensus: number }) => !match.confirmed && match.consensus === consensusLevel)
                      .map((match: { similarity: number }) => match.similarity);
                    
                    if (similaritiesConfirmed.length === 0 && similaritiesRejected.length === 0) {
                      return null;
                    }
                    
                    similaritiesConfirmed.sort((a: number, b: number) => a - b);
                    similaritiesRejected.sort((a: number, b: number) => a - b);
                    
                    let bestThreshold = 0.5;
                    let bestAccuracy = 0;
                    
                    const minThreshold = 0.3;
                    const maxThreshold = 0.95;
                    
                    for (let threshold = minThreshold; threshold <= maxThreshold; threshold += 0.01) {
                      const truePositives = similaritiesConfirmed.filter((s: number) => s >= threshold).length;
                      const trueNegatives = similaritiesRejected.filter((s: number) => s < threshold).length;
                      const accuracy = (truePositives + trueNegatives) / (similaritiesConfirmed.length + similaritiesRejected.length);
                      
                      if (accuracy > bestAccuracy) {
                        bestAccuracy = accuracy;
                        bestThreshold = threshold;
                      }
                    }
                    
                    return {
                      threshold: bestThreshold,
                      accuracy: bestAccuracy,
                      confirmedCount: similaritiesConfirmed.length,
                      rejectedCount: similaritiesRejected.length
                    };
                  };
                  

                  // Calculate thresholds for each consensus level
                  const thresholds = {
                    three: calculateThreshold(3),
                    two: calculateThreshold(2),
                    one: calculateThreshold(1)
                  };

                  // Render the result
                  return (
                    <Box>
                      {thresholds.three && (
                        <Text mb={2}>
                          3 Families: {(thresholds.three.threshold * 100).toFixed(1)}% 
                          <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                            ({thresholds.three.confirmedCount} confirmed, {thresholds.three.rejectedCount} rejected)
                          </Text>
                        </Text>
                      )}
                      {thresholds.two && (
                        <Text mb={2}>
                          2 Families: {(thresholds.two.threshold * 100).toFixed(1)}%
                          <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                            ({thresholds.two.confirmedCount} confirmed, {thresholds.two.rejectedCount} rejected)
                          </Text>
                        </Text>
                      )}
                      {thresholds.one && (
                        <Text>
                          1 Family: {(thresholds.one.threshold * 100).toFixed(1)}%
                          <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                            ({thresholds.one.confirmedCount} confirmed, {thresholds.one.rejectedCount} rejected)
                          </Text>
                        </Text>
                      )}
                    </Box>
                  );
                })()}
              </StatNumber>
              <StatHelpText>
                Estimated optimal thresholds by model family agreement
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>Model Performance</Tab>
          <Tab>Source Distribution</Tab>
          <Tab>Similarity Analysis</Tab>
          <Tab>Confirmed vs. Rejected</Tab>
          <Tab>Model Comparison</Tab>
        </TabList>
        
        <TabPanels>
          {/* Model Performance Tab */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Model Count Chart */}
              <Box bg="white" p={4} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>Model Usage</Heading>
                <Box height="400px">
                  <Bar 
                    data={{
                      labels: analyticsData.modelStats.map(stat => stat.name),
                      datasets: [
                        {
                          label: 'Match Count',
                          data: analyticsData.modelStats.map(stat => stat.count),
                          backgroundColor: '#8884d8',
                        },
                      ],
                    }} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                    }} 
                  />
                </Box>
              </Box>
              
              {/* Model Similarity Chart */}
              <Box bg="white" p={4} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>Average Similarity by Model</Heading>
                <Box height="400px">
                  <Bar 
                    data={{
                      labels: analyticsData.modelStats.map(stat => stat.name),
                      datasets: [
                        {
                          label: 'Average Similarity',
                          data: analyticsData.modelStats.map(stat => stat.avgSimilarity),
                          backgroundColor: '#82ca9d',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 1,
                          ticks: {
                            callback: function(tickValue: string | number) {
                              const value = Number(tickValue);
                              return (value * 100).toFixed(0) + '%';
                            }
                          }
                        }
                      },
                      plugins: {
                        tooltip: {
                          callbacks: {
                            label: function(context: any) {
                              const value = context.raw as number;
                              return (value * 100).toFixed(2) + '%';
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            </SimpleGrid>
          </TabPanel>
          
          {/* Source Distribution Tab */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Source Pie Chart */}
              <Box bg="white" p={4} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>Match Sources</Heading>
                <Box height="400px" display="flex" justifyContent="center" alignItems="center">
                  <div style={{ width: '80%', height: '80%' }}>
                    <Pie 
                      data={{
                        labels: analyticsData.sourceStats.map(stat => stat.name),
                        datasets: [
                          {
                            data: analyticsData.sourceStats.map(stat => stat.value),
                            backgroundColor: COLORS.slice(0, analyticsData.sourceStats.length),
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                      }}
                    />
                  </div>
                </Box>
              </Box>
              
              {/* Source Bar Chart */}
              <Box bg="white" p={4} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>Matches by Source</Heading>
                <Box height="400px">
                  <Bar 
                    data={{
                      labels: analyticsData.sourceStats.map(stat => stat.name),
                      datasets: [
                        {
                          label: 'Match Count',
                          data: analyticsData.sourceStats.map(stat => stat.count),
                          backgroundColor: '#8884d8',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                    }}
                  />
                </Box>
              </Box>
            </SimpleGrid>
          </TabPanel>
          
          {/* Similarity Analysis Tab */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Similarity Distribution */}
              <Box bg="white" p={4} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>Similarity Distribution</Heading>
                <Box height="400px">
                  <Bar 
                    data={{
                      labels: analyticsData.similarityDistribution.map(stat => stat.range),
                      datasets: [
                        {
                          label: 'Match Count',
                          data: analyticsData.similarityDistribution.map(stat => stat.count),
                          backgroundColor: '#8884d8',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                    }}
                  />
                </Box>
              </Box>
              
              {/* Consensus Stats */}
              <Box bg="white" p={4} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>Model Family Consensus</Heading>
                <Box height="400px">
                  <Bar 
                    data={{
                      labels: analyticsData.consensusStats.map(stat => `${stat.modelFamilies} Families`),
                      datasets: [
                        {
                          label: 'Match Count',
                          data: analyticsData.consensusStats.map(stat => stat.count),
                          backgroundColor: '#82ca9d',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                    }}
                  />
                </Box>
              </Box>
            </SimpleGrid>
          </TabPanel>
          
          {/* Confirmed vs. Rejected Tab */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Similarity Comparison */}
              <Box bg="white" p={4} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>Similarity: Confirmed vs. Rejected</Heading>
                <Box height="400px">
                  <Bar 
                    data={{
                      labels: ['Confirmed', 'Rejected'],
                      datasets: [
                        {
                          label: 'Average Similarity',
                          data: [
                            analyticsData.confirmedAvgSimilarity,
                            analyticsData.rejectedAvgSimilarity
                          ],
                          backgroundColor: ['#4CAF50', '#F44336'],
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 1,
                          ticks: {
                            callback: function(tickValue: string | number) {
                              const value = Number(tickValue);
                              return (value * 100).toFixed(0) + '%';
                            }
                          }
                        }
                      },
                      plugins: {
                        tooltip: {
                          callbacks: {
                            label: function(context: any) {
                              const value = context.raw as number;
                              return (value * 100).toFixed(2) + '%';
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Consensus Comparison */}
              <Box bg="white" p={4} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>Model Consensus: Confirmed vs. Rejected</Heading>
                <Box height="400px">
                  <Bar 
                    data={{
                      labels: analyticsData.consensusComparison.map(stat => `${stat.modelFamilies} Families`),
                      datasets: [
                        {
                          label: 'Confirmed',
                          data: analyticsData.consensusComparison.map(stat => stat.confirmedCount),
                          backgroundColor: '#4CAF50',
                        },
                        {
                          label: 'Rejected',
                          data: analyticsData.consensusComparison.map(stat => stat.rejectedCount),
                          backgroundColor: '#F44336',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                    }}
                  />
                </Box>
              </Box>
              
              {/* Model Performance Comparison */}
              <Box bg="white" p={4} borderRadius="md" boxShadow="md" gridColumn="span 2">
                <Heading size="md" mb={4}>Model Performance: Confirmed vs. Rejected</Heading>
                <Box height="400px">
                  <Bar 
                    data={{
                      labels: analyticsData.modelComparison.map(model => model.name),
                      datasets: [
                        {
                          label: 'Lowest Confirmed Similarity',
                          data: analyticsData.modelComparison.map(model => model.lowestConfirmedSimilarity),
                          backgroundColor: '#4CAF50',
                        },
                        {
                          label: 'Highest Rejected Similarity',
                          data: analyticsData.modelComparison.map(model => model.highestRejectedSimilarity),
                          backgroundColor: '#F44336',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 1,
                          ticks: {
                            callback: function(tickValue: string | number) {
                              const value = Number(tickValue);
                              return (value * 100).toFixed(0) + '%';
                            }
                          }
                        }
                      },
                      plugins: {
                        tooltip: {
                          callbacks: {
                            label: function(context: any) {
                              const value = context.raw as number;
                              return (value * 100).toFixed(2) + '%';
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            </SimpleGrid>

            <Box bg="white" p={4} borderRadius="md" boxShadow="md" mt={6} gridColumn="span 2">
              <Heading size="md" mb={4}>Consensus Breakdown</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                {analyticsData.consensusComparison.map(stat => (
                  <Card key={stat.modelFamilies}>
                    <CardBody>
                      <Stat>
                        <StatLabel>{stat.modelFamilies} Model Families</StatLabel>
                        <Flex justifyContent="space-between" mt={2}>
                          <Box>
                            <Text fontWeight="bold" color="green.500">
                              {stat.confirmedCount} Confirmed
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {analyticsData.confirmedMatches > 0 
                                ? `${(stat.confirmedCount / analyticsData.confirmedMatches * 100).toFixed(1)}%`
                                : '0%'} of confirmed
                            </Text>
                          </Box>
                          <Box>
                            <Text fontWeight="bold" color="red.500">
                              {stat.rejectedCount} Rejected
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {analyticsData.rejectedMatches > 0 
                                ? `${(stat.rejectedCount / analyticsData.rejectedMatches * 100).toFixed(1)}%`
                                : '0%'} of rejected
                            </Text>
                          </Box>
                        </Flex>
                        <Text mt={2} fontWeight="bold">
                          Confirmation Rate: {
                            (stat.confirmedCount + stat.rejectedCount > 0)
                              ? `${(stat.confirmedCount / (stat.confirmedCount + stat.rejectedCount) * 100).toFixed(1)}%`
                              : '0%'
                          }
                        </Text>
                      </Stat>
                    </CardBody>
                  </Card>
                ))}
              </SimpleGrid>
            </Box>
          </TabPanel>

          {/* Model Comparison Tab */}
          <TabPanel>
            <Box bg="white" p={4} borderRadius="md" boxShadow="md">
              <Heading size="md" mb={4}>Model Performance Comparison</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                {analyticsData.modelComparison.map(model => (
                  <Card key={model.name}>
                    <CardBody>
                      <Stat>
                        <StatLabel>{model.name}</StatLabel>
                        <Flex direction="column" gap={2} mt={2}>
                          <Box p={2} bg="red.50" borderRadius="md">
                            <Text fontWeight="bold" color="red.500">
                              Highest Rejected: {(model.highestRejectedSimilarity * 100).toFixed(1)}%
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              Consensus: {model.highestRejectedConsensus} families
                            </Text>
                          </Box>
                          
                          <Box p={2} bg="green.50" borderRadius="md">
                            <Text fontWeight="bold" color="green.500">
                              Lowest Confirmed: {(model.lowestConfirmedSimilarity * 100).toFixed(1)}%
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              Consensus: {model.lowestConfirmedConsensus} families
                            </Text>
                          </Box>
                          
                          <Box p={2} bg="blue.50" borderRadius="md">
                            <Text fontWeight="bold" color="blue.500">
                              Threshold: {((model.lowestConfirmedSimilarity + model.highestRejectedSimilarity) / 2 * 100).toFixed(1)}%
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              Estimated optimal threshold
                            </Text>
                          </Box>
                        </Flex>
                        
                        <Text mt={2} fontWeight="bold">
                          Confirmation Rate: {
                            (model.confirmedCount + model.rejectedCount > 0)
                              ? `${(model.confirmedCount / (model.confirmedCount + model.rejectedCount) * 100).toFixed(1)}%`
                              : '0%'
                          }
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Based on {model.confirmedCount + model.rejectedCount} total matches
                        </Text>
                      </Stat>
                    </CardBody>
                  </Card>
                ))}
              </SimpleGrid>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}; 